/**
 * Payload / response helpers for valuation/engine-valuation/compute/
 */

const pickId = (obj) => {
  if (obj == null || obj === '') return null;
  if (typeof obj === 'number' || typeof obj === 'string') return obj;
  return obj.id != null ? obj.id : null;
};

const shopVisitCode = (shopVisitStatus) => {
  if (shopVisitStatus == null || shopVisitStatus === '') return null;
  if (typeof shopVisitStatus === 'object') {
    return shopVisitStatus.code || shopVisitStatus.value || null;
  }
  return shopVisitStatus;
};

/** Selected metric keys for compute/update payload (BE uses these for projection_to rules) */
export const buildSelectedMetrics = (valuationCrud = {}) => {
  const metrics = [];
  if (valuationCrud.show_bv_cmv !== false) metrics.push('bv_cmv');
  if (parseInt(valuationCrud.utility_type, 10) === 2 || valuationCrud.utility_type === 'Future Value') {
    metrics.push('fbv');
  }
  if (valuationCrud.is_blr_mlr) metrics.push('blr');
  if (valuationCrud.is_future_blr) metrics.push('fblr');
  return metrics;
};

/** Map saved/compute `metrics: ["bv_cmv", ...]` into form checkbox flags */
export const metricsFlagsFromSelection = (selectedMetrics) => {
  const list = Array.isArray(selectedMetrics) ? selectedMetrics : [];
  return {
    show_bv_cmv: list.length ? list.includes('bv_cmv') : true,
    utility_type: list.includes('fbv') ? 2 : 1,
    is_blr_mlr: list.includes('blr'),
    is_future_blr: list.includes('fblr'),
  };
};

/** Build POST/PUT body for engine-valuation compute / recompute */
export const buildEngineComputePayload = (valuationCrud = {}) => {
  const metrics = buildSelectedMetrics(valuationCrud);
  const payload = {
    serial_number: valuationCrud.serial_number || '',
    engine_type: { id: pickId(valuationCrud.engine_type) },
    date_of_manufacture: valuationCrud.date_of_manufacture || null,
    condition: valuationCrud.condition,
    qec_discount: valuationCrud.qec_discount != null && valuationCrud.qec_discount !== ''
      ? Number(valuationCrud.qec_discount)
      : 0,
    shop_visit_status: shopVisitCode(valuationCrud.shop_visit_status),
    inflation_rate: valuationCrud.escalation_percentage != null && valuationCrud.escalation_percentage !== ''
      ? Number(valuationCrud.escalation_percentage)
      : 0,
    value_as_of: valuationCrud.base_value_date,
    premium_for_new: valuationCrud.premium_for_new || 'no',
    premium_percentage: valuationCrud.premium_pct != null && valuationCrud.premium_pct !== ''
      ? Number(valuationCrud.premium_pct)
      : 0,
    metrics,
  };

  const qecId = pickId(valuationCrud.qec_type);
  if (qecId != null) {
    payload.qec_type = { id: qecId };
  }

  const needsProjection = metrics.includes('fbv') || metrics.includes('fblr');
  if (needsProjection && valuationCrud.projection_to) {
    payload.projection_to = valuationCrud.projection_to;
  }

  return payload;
};

/**
 * Normalize compute (or log) response into the shape used by EngineValuationOutput
 * and EngineFutureProjectionsModal. Metric checkboxes prefer explicit form flags,
 * then inputs.metrics from the API (never invent FBV/BLR from output values).
 */
export const normalizeEngineComputeResponse = (data = {}, existingCrud = {}) => {
  const ev = data.engine_valuation || {};
  const metrics = ev.metrics || {};
  const bv = metrics.bv || {};
  const cmv = metrics.cmv || {};
  const inputs = ev.inputs || {};
  const projections = ev.projections || {};
  const projectionValues = projections.values || {};

  const selectedMetrics = inputs.metrics || data.metrics;
  const fromMetrics = Array.isArray(selectedMetrics)
    ? metricsFlagsFromSelection(selectedMetrics)
    : null;

  const shopVisit = inputs.shop_visit_status;
  let shop_visit_status = existingCrud.shop_visit_status;
  if (shopVisit != null) {
    shop_visit_status = typeof shopVisit === 'object'
      ? (shopVisit.code || shopVisit.value)
      : shopVisit;
  }

  // Prefer API inputs (including explicit null) so listing reload does not invent dates
  const resolveDate = (key) => {
    if (Object.prototype.hasOwnProperty.call(inputs, key)) {
      return inputs[key] || null;
    }
    if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
      return data[key];
    }
    return existingCrud[key] != null && existingCrud[key] !== '' ? existingCrud[key] : null;
  };

  return {
    ...existingCrud,
    ...data,
    id: data.log_id || existingCrud.id,
    log_id: data.log_id || existingCrud.log_id,
    asset_type: 2,
    base_value_date: data.value_as_of || inputs.value_as_of || existingCrud.base_value_date || null,
    projection_to: resolveDate('projection_to'),
    projection_label: data.projection_label,
    escalation_percentage: ev.inflation_rate != null
      ? ev.inflation_rate
      : (inputs.inflation_rate != null ? inputs.inflation_rate : existingCrud.escalation_percentage),
    serial_number: inputs.serial_number != null ? inputs.serial_number : existingCrud.serial_number,
    engine_type: inputs.engine_type || existingCrud.engine_type,
    date_of_manufacture: resolveDate('date_of_manufacture'),
    qec_type: inputs.qec_type || existingCrud.qec_type,
    condition: inputs.condition_applied || inputs.condition || existingCrud.condition,
    qec_discount: inputs.qec_discount != null ? inputs.qec_discount : existingCrud.qec_discount,
    shop_visit_status,
    premium_for_new: inputs.premium_for_new != null ? inputs.premium_for_new : existingCrud.premium_for_new,
    premium_pct: inputs.premium_percentage != null ? inputs.premium_percentage : existingCrud.premium_pct,
    // Dial cards
    base_value: bv.half_life,
    full_life_base_value: bv.full_life,
    cmv_value: cmv.half_life,
    full_life_cmv_value: cmv.full_life,
    blr_value: metrics.blr,
    mlr_value: metrics.mlr,
    // Keep nested payload for projections UI
    engine_valuation: ev,
    future_base_value_rows: projectionValues.future_base_value || [],
    future_blr_rows: projectionValues.future_blr || [],
    projection_graph: projections.graph || null,
    // Metric visibility: form flags → inputs.metrics → safe defaults (no false FBV/BLR)
    show_bv_cmv: existingCrud.show_bv_cmv !== undefined
      ? existingCrud.show_bv_cmv
      : (fromMetrics ? fromMetrics.show_bv_cmv : true),
    is_blr_mlr: existingCrud.is_blr_mlr !== undefined
      ? !!existingCrud.is_blr_mlr
      : (fromMetrics ? fromMetrics.is_blr_mlr : false),
    is_future_blr: existingCrud.is_future_blr !== undefined
      ? !!existingCrud.is_future_blr
      : (fromMetrics ? fromMetrics.is_future_blr : false),
    utility_type: existingCrud.utility_type != null
      ? existingCrud.utility_type
      : (fromMetrics ? fromMetrics.utility_type : 1),
  };
};
